import { sortBy } from 'lib/array/arrayUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import type {
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
} from 'tripPlanner/api/types'
import { isExperienceSavedData, isHotelSavedData, type ExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type { CharterFlightBundleSelection } from 'tripPlanner/utils/charterBundleHydration'
import {
  isCartPanelExperienceDateRequired,
  isExperienceBuyNowBookLaterBookingType,
  resolveCartPanelExperienceAvailabilityStatus,
  resolveCartPanelItemCartStatus,
  type CartPanelExperienceUnavailableStatus,
  type CartPanelExperienceTicket,
  type CartPanelItemCartStatus,
} from './cartPanelUtils'

type ItineraryItem = TripItineraryItem | TripItemWithoutDay

export interface TripChatAccommodationCartCandidate {
  productType: 'accommodation'
  tripItemId: number
  offerId: string
  packageId: string | undefined
  cartItemId: string | undefined
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  isBooked: boolean
  name: string
  imageId: string | undefined
  roomImageId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number | undefined
  roomName: string | undefined
  roomRateId: string | undefined
  occupancy: string | undefined
  rawOccupancy: App.Occupants | undefined
  occupancies?: Array<App.Occupants>
  packageAutoSelected: boolean | undefined
  availableDurations: Array<number> | undefined
  isBedbankHotel: boolean
  savedPrice: number | undefined
  savedMemberPrice: number | undefined
  savedValue: number | undefined
  /** Charter flight bundle selection (Maldives MEL↔MLE), when this hotel is a charter bundle. */
  charterFlightBundle?: CharterFlightBundleSelection
}

export interface TripChatExperienceCartCandidate {
  productType: 'experience'
  tripItemId: number
  experienceId: string
  cartItemId: string | undefined
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  isBooked: boolean
  name: string
  imageId: string | undefined
  date: string | undefined
  itineraryDate: string | undefined
  time: string | undefined
  tickets: Array<CartPanelExperienceTicket>
  locationName: string | undefined
  pickupPointId: string | undefined
  redemptionLocationId: string | undefined
  isBuyNowBookLater: boolean
  bookingType: string | undefined
  savedPrice: number | undefined
  savedMemberPrice: number | undefined
  savedValue: number | undefined
}

export type TripChatCartCandidate =
  | TripChatAccommodationCartCandidate
  | TripChatExperienceCartCandidate

function toExperienceTickets(savedData: ExperienceSavedData): Array<CartPanelExperienceTicket> {
  return (savedData.tickets ?? []).map(ticket => ({
    ticketId: ticket.ticketId,
    name: ticket.name,
    count: ticket.count,
    date: ticket.date ?? savedData.date,
  }))
}

function getExperienceAvailabilityCartStatus(
  savedData: ExperienceSavedData,
): CartPanelExperienceUnavailableStatus | undefined {
  return resolveCartPanelExperienceAvailabilityStatus(savedData.experienceAvailabilityStatus)
}

function getExperienceCartStatus(
  item: ItineraryItem,
  savedData: ExperienceSavedData,
): CartPanelItemCartStatus {
  const unavailableStatus = getExperienceAvailabilityCartStatus(savedData)
  if (unavailableStatus) return unavailableStatus

  const resolved = resolveCartPanelItemCartStatus(item.cartStatus)
  const isBuyNowBookLater = isExperienceBuyNowBookLaterBookingType(savedData.bookingType)
  if (resolved === 'active' && !savedData.date && isCartPanelExperienceDateRequired(savedData.bookingType, isBuyNowBookLater, false)) {
    return 'needs_dates'
  }
  if (resolved === 'active' && savedData.bookingType === 'CALENDAR-TIMESLOTS' && savedData.date && !savedData.time && !isBuyNowBookLater) {
    return 'needs_time'
  }
  return resolved ?? 'pending_hydration'
}

function getAccommodationRoomName(savedData: NonNullable<ItineraryItem['savedItemData']>): string | undefined {
  if (!isHotelSavedData(savedData)) return undefined
  if (savedData.offerType === 'bedbank_hotel' || savedData.source === 'spp') {
    return savedData.roomTypeName ?? savedData.packageName
  }
  return savedData.packageName ?? savedData.roomTypeName
}

function getAccommodationRoomImageId(savedData: NonNullable<ItineraryItem['savedItemData']>): string | undefined {
  if (!isHotelSavedData(savedData) || !('roomImageIds' in savedData)) return undefined
  return Array.isArray(savedData.roomImageIds) ? savedData.roomImageIds[0] : undefined
}

export function extractTripChatCartCandidates(
  itineraryData: TripItineraryData | undefined,
): Array<TripChatCartCandidate> {
  if (!itineraryData?.stops) return []

  const results: Array<TripChatCartCandidate> = []
  const seenItemIds = new Set<number>()

  const processItem = (item: ItineraryItem, itineraryDate?: string) => {
    if (item.isBooked) return
    if (seenItemIds.has(item.id)) return

    const savedData = item.savedItemData

    if (item.type === 'ACCOMMODATION' && isHotelSavedData(savedData)) {
      seenItemIds.add(item.id)
      const status = resolveCartPanelItemCartStatus(item.cartStatus) ?? 'pending_hydration'

      results.push({
        productType: 'accommodation',
        tripItemId: item.id,
        offerId: savedData.offerId,
        packageId: savedData.packageId,
        cartItemId: item.cartItemId,
        cartStatus: status,
        cartExcluded: item.cartExcluded ?? false,
        isBooked: item.isBooked ?? false,
        name: item.name ?? '',
        imageId: savedData.imageIds?.[0],
        roomImageId: getAccommodationRoomImageId(savedData),
        checkIn: savedData.checkIn,
        checkOut: savedData.checkOut,
        duration: savedData.packageDuration,
        roomName: getAccommodationRoomName(savedData),
        roomRateId: savedData.roomRateId,
        occupancy: savedData.occupancies?.[0] ? formatOccupantsShort(savedData.occupancies[0]) : undefined,
        rawOccupancy: savedData.occupancies?.[0],
        occupancies: savedData.occupancies,
        packageAutoSelected: savedData.packageAutoSelected,
        availableDurations: savedData.availableDurations ?? item.availableDurations,
        isBedbankHotel: savedData.offerType === 'bedbank_hotel' || savedData.source === 'spp',
        savedPrice: savedData.price,
        savedMemberPrice: savedData.memberPrice,
        savedValue: savedData.value,
        charterFlightBundle: savedData.source === 'offer' ? savedData.charterFlightBundle : undefined,
      })
      return
    }

    if (item.type === 'EXPERIENCE' && isExperienceSavedData(savedData)) {
      seenItemIds.add(item.id)
      results.push({
        productType: 'experience',
        tripItemId: item.id,
        experienceId: savedData.experienceId,
        cartItemId: item.cartItemId,
        cartStatus: getExperienceCartStatus(item, savedData),
        cartExcluded: item.cartExcluded ?? false,
        isBooked: item.isBooked ?? false,
        name: item.name ?? '',
        imageId: savedData.imageIds?.[0],
        date: savedData.date,
        itineraryDate,
        time: savedData.time,
        tickets: toExperienceTickets(savedData),
        locationName: item.itemPlaceDetail?.primaryTypeDisplayName ?? savedData.location?.city,
        pickupPointId: savedData.pickupPointId,
        redemptionLocationId: savedData.redemptionLocationId,
        isBuyNowBookLater: isExperienceBuyNowBookLaterBookingType(savedData.bookingType),
        bookingType: savedData.bookingType,
        savedPrice: savedData.price,
        savedMemberPrice: savedData.memberPrice,
        savedValue: savedData.value,
      })
    }
  }

  for (const stop of itineraryData.stops) {
    for (const day of stop.days) {
      for (const item of day.items) {
        processItem(item, day.date)
      }
    }
    for (const item of stop.itemsWithoutDay ?? []) {
      processItem(item)
    }
  }

  return results
}

export function toTripChatAccommodationCartSignature(item: TripChatAccommodationCartCandidate): string | undefined {
  if (!item.cartItemId) return undefined
  return JSON.stringify({
    productType: item.productType,
    tripItemId: item.tripItemId,
    cartItemId: item.cartItemId,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
    offerId: item.offerId,
    packageId: item.packageId,
    roomRateId: item.roomRateId,
    duration: item.duration,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    occupancy: item.rawOccupancy,
    occupancies: item.occupancies,
    charterFlightBundle: item.charterFlightBundle,
  })
}

export function toTripChatExperienceCartSignature(item: TripChatExperienceCartCandidate): string | undefined {
  if (!item.cartItemId) return undefined
  return JSON.stringify({
    productType: item.productType,
    tripItemId: item.tripItemId,
    cartItemId: item.cartItemId,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
    experienceId: item.experienceId,
    date: item.date,
    time: item.time,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    tickets: sortBy(
      item.tickets.map(ticket => ({
        ticketId: ticket.ticketId,
        name: ticket.name,
        count: ticket.count,
        date: ticket.date,
      })),
      ticket => `${ticket.ticketId}|${ticket.name}|${ticket.date ?? ''}`,
      'asc',
    ),
  })
}

export function toTripChatCartCandidateSignature(item: TripChatCartCandidate): string | undefined {
  if (item.productType === 'accommodation') return toTripChatAccommodationCartSignature(item)
  return toTripChatExperienceCartSignature(item)
}

export function getTripChatCartCandidateProjectionSignature(
  multiCartId: string | undefined,
  candidates: Array<TripChatCartCandidate>,
  flightSignatures: Array<string>,
): string {
  return JSON.stringify({
    multiCartId,
    candidates: sortBy(
      candidates.map(item => ({
        productType: item.productType,
        tripItemId: item.tripItemId,
        cartItemId: item.cartItemId,
        cartStatus: item.cartStatus,
        cartExcluded: item.cartExcluded,
      })),
      item => `${item.productType}|${item.tripItemId}`,
      'asc',
    ),
    flights: sortBy(flightSignatures, signature => signature, 'asc'),
  })
}
